import React, { useState, useEffect } from "react";
import { ExternalLink, X, ChevronRight, Sparkles, FolderKanban } from "lucide-react";
import projectsService from "./services/projectsService";

export default function ProjectsSection() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    async function loadProjects() {
      try {
        setLoading(true);
        const data = await projectsService.getPublished();
        setProjects(data || []);
      } catch (err) {
        console.error("Error loading public projects:", err);
      } finally {
        setLoading(false);
      }
    }
    loadProjects();
  }, []);

  // Derive unique categories from active published projects
  const categories = [
    "all",
    ...Array.from(
      new Set(
        projects
          .map((p) => p.category)
          .filter((cat) => Boolean(cat) && typeof cat === "string" && cat.trim().length > 0)
      )
    ),
  ];

  const filteredProjects =
    activeCategory === "all"
      ? projects
      : projects.filter(
          (p) => p.category && p.category.toLowerCase() === activeCategory.toLowerCase()
        );

  return (
    <section
      id="projects"
      className="relative py-24 bg-gradient-to-b from-white via-slate-50/70 to-white overflow-hidden"
    >
      {/* Background Decorative Glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -right-32 w-96 h-96 bg-[#9BB7A3]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 -left-32 w-96 h-96 bg-[#9BB7A3]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-[#9BB7A3] font-bold tracking-[4px] uppercase text-xs sm:text-sm block">
            Crafted Portfolios
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-800 mt-3">
            Featured Projects & Interiors
          </h2>
          <div className="w-20 h-1 bg-[#9BB7A3] mx-auto mt-4 rounded-full"></div>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-sm sm:text-base">
            Explore our curated residential residences, commercial architecture, and complete turnkey transformations.
          </p>
        </div>

        {/* Category Filter Tabs */}
        {categories.length > 1 && (
          <div className="flex items-center justify-center gap-2 mb-12 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-300 capitalize tracking-wide ${
                  activeCategory.toLowerCase() === cat.toLowerCase()
                    ? "bg-[#9BB7A3] text-white shadow-md shadow-[#9BB7A3]/25 scale-105"
                    : "bg-white text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 shadow-sm"
                }`}
              >
                {cat === "all" ? "All Works" : cat}
              </button>
            ))}
          </div>
        )}

        {/* Projects Grid */}
        {loading ? (
          <div className="py-20 flex justify-center items-center">
            <div className="w-10 h-10 border-3 border-[#9BB7A3]/30 border-t-[#9BB7A3] rounded-full animate-spin"></div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-16 bg-white/60 backdrop-blur-md rounded-3xl border border-slate-100 p-8 max-w-md mx-auto shadow-sm">
            <div className="w-16 h-16 rounded-2xl bg-[#9BB7A3]/10 text-[#9BB7A3] flex items-center justify-center mx-auto mb-4">
              <FolderKanban size={32} />
            </div>
            <h3 className="text-lg font-bold text-slate-800">No Projects Found</h3>
            <p className="text-xs text-slate-500 mt-1">
              New project case studies will be published here shortly.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                onClick={() => setSelectedProject(project)}
                className="
                  group
                  cursor-pointer
                  bg-white
                  rounded-[28px]
                  border border-slate-100
                  shadow-md
                  hover:shadow-2xl
                  hover:-translate-y-2
                  transition-all
                  duration-500
                  overflow-hidden
                  flex flex-col
                "
              >
                {/* Cover Image Container */}
                <div className="relative h-64 w-full overflow-hidden bg-slate-100">
                  {project.cover_image_url ? (
                    <img
                      src={project.cover_image_url}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-tr from-slate-100 to-slate-200 text-slate-400">
                      <FolderKanban size={48} />
                    </div>
                  )}

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-80 group-hover:opacity-60 transition-opacity"></div>

                  {/* Badges */}
                  <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                    <span className="px-3.5 py-1 rounded-full text-[11px] font-extrabold bg-white/90 backdrop-blur-md text-slate-800 shadow-sm uppercase tracking-wider">
                      {project.category || "Design Work"}
                    </span>
                    {project.featured && (
                      <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-[#9BB7A3] text-white shadow-sm flex items-center gap-1">
                        <Sparkles size={12} /> Featured
                      </span>
                    )}
                  </div>

                  {/* Quick Click Hint on Hover */}
                  <div className="absolute bottom-4 right-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                    <span className="px-3 py-1.5 rounded-full bg-white text-slate-900 text-xs font-bold shadow-lg flex items-center gap-1">
                      View Project <ChevronRight size={14} />
                    </span>
                  </div>
                </div>

                {/* Content Block */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 group-hover:text-[#9BB7A3] transition-colors line-clamp-1">
                      {project.title}
                    </h3>
                    <p className="text-slate-600 text-xs sm:text-sm mt-2 line-clamp-2 leading-relaxed">
                      {project.short_description || project.description || "Bespoke architectural execution and interior design."}
                    </p>
                  </div>

                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs font-bold text-[#9BB7A3] uppercase tracking-wider group-hover:underline">
                      Explore Case Study
                    </span>
                    <ChevronRight size={16} className="text-[#9BB7A3] transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Project Detail Lightbox Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fadeIn"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="
              relative
              w-full
              max-w-3xl
              max-h-[90vh]
              bg-white
              rounded-[32px]
              overflow-hidden
              shadow-2xl
              flex flex-col
              animate-[popup_0.3s_ease]
            "
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedProject(null)}
              className="
                absolute
                top-4
                right-4
                w-10
                h-10
                rounded-full
                bg-black/50
                hover:bg-black/80
                text-white
                flex items-center justify-center
                z-20
                transition
              "
            >
              <X size={20} />
            </button>

            {/* Modal Image Header */}
            <div className="relative h-72 sm:h-96 w-full bg-slate-900 shrink-0">
              {selectedProject.cover_image_url ? (
                <img
                  src={selectedProject.cover_image_url}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-500">
                  <FolderKanban size={64} />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#9BB7A3] text-white uppercase tracking-wider">
                  {selectedProject.category || "Interior Architecture"}
                </span>
                <h2 className="text-2xl sm:text-3xl font-black mt-2 text-white">
                  {selectedProject.title}
                </h2>
              </div>
            </div>

            {/* Modal Content Scroll Area */}
            <div className="p-6 sm:p-8 overflow-y-auto space-y-6">
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                  Project Overview
                </h4>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                  {selectedProject.description || selectedProject.short_description || "Detailed case study description for this design project."}
                </p>
              </div>

              {/* Additional Project Gallery Images */}
              {selectedProject.project_images && selectedProject.project_images.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                    Project Gallery
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selectedProject.project_images.map((img, idx) => (
                      <div key={idx} className="rounded-xl overflow-hidden aspect-video bg-slate-100 border border-slate-200">
                        <img src={img.image_url} alt={img.alt_text || `View ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-slate-100">
                {selectedProject.project_url ? (
                  <a
                    href={selectedProject.project_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#9BB7A3] text-white font-bold text-xs uppercase tracking-wider hover:bg-[#86a38e] transition shadow-md"
                  >
                    <span>Visit Live Project</span>
                    <ExternalLink size={14} />
                  </a>
                ) : (
                  <a
                    href="#contact"
                    onClick={() => setSelectedProject(null)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#9BB7A3] text-white font-bold text-xs uppercase tracking-wider hover:bg-[#86a38e] transition shadow-md"
                  >
                    <span>Enquire About Similar Design</span>
                    <ChevronRight size={14} />
                  </a>
                )}

                <button
                  onClick={() => setSelectedProject(null)}
                  className="px-6 py-3 rounded-full border border-slate-200 text-slate-700 font-bold text-xs uppercase tracking-wider hover:bg-slate-50 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
